import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import Index from "./pages/Index";
import { lazyWithReload } from "./lib/lazyWithReload";
// Non-home routes load on demand to keep the first bundle small
const Blog = lazyWithReload(() => import("./pages/Blog"));
const BlogDetail = lazyWithReload(() => import("./pages/BlogDetail"));
const ProjectsPage = lazyWithReload(() => import("./pages/Projects"));
const ExperiencePage = lazyWithReload(() => import("./pages/Experience"));
const CertificationsPage = lazyWithReload(() => import("./pages/Certifications"));
const SkillsPage = lazyWithReload(() => import("./pages/Skills"));
const AboutPage = lazyWithReload(() => import("./pages/About"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));
// Warm the inner pages' code a few seconds after load (idle time), so the first click
// into Blogs / a post / Projects doesn't wait on a download
if (typeof window !== "undefined") {
  const warm = () => {
    import("./pages/BlogDetail").catch(() => {});
    import("./pages/Blog").catch(() => {});
    import("./pages/Projects").catch(() => {});
  };
  window.addEventListener("load", () => setTimeout(() => ("requestIdleCallback" in window ? requestIdleCallback(warm) : warm()), 3500), { once: true });
}
import { SmoothScroll } from "./components/SmoothScroll";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { CustomCursor } from "./components/CustomCursor";
import clickSound from "./assets/click.mp3";

// Route change: jump to the top only once the old page has faded out. Scrolling on the
// click itself showed the top of the old page (the home hero) for a moment before it left.
const scrollToTop = () => {
  const lenis = (window as any).lenis;
  if (lenis) lenis.scrollTo(0, { immediate: true });
  else window.scrollTo(0, 0);
};

const queryClient = new QueryClient();

// Wrapper for page transitions
const OldBlogRedirect = () => <Navigate to={`/blogs/${useParams().slug ?? ""}`} replace />;

const PageWrapper = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    // Quick fade out only. Each page animates its own content in (home: <Reveal>, others:
    // useStaggerIn); a page-level fade on top multiplied with those and left a near-blank gap.
    exit={{ opacity: 0, transition: { duration: 0.15, ease: "easeOut" } }}
    // min-height prevents the page from collapsing to 0 during AnimatePresence's
    // "wait" mode brief empty state, which would trigger mobile URL bar toggling
    // and cause the fixed dock to jump.
    // clip (not hidden): hidden turns this into a scroll box that shows its own scrollbar
    style={{ minHeight: "100dvh", overflowX: "clip" }}
  >
    {/* Suspense lives inside each page: around AnimatePresence it would stall the exit animation */}
    <React.Suspense fallback={null}>{children}</React.Suspense>
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  // The prerendered hero sits outside this tree, so fade it out with the home page
  React.useEffect(() => {
    if (location.pathname === "/") return;
    document.getElementById("hero-shell")?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: "ease-out", fill: "forwards" });
  }, [location.pathname]);

  return (
    <div className="min-h-[100dvh] w-full flex flex-col" style={{ overflowX: "clip" }}>
      <AnimatePresence mode="wait" initial={false} onExitComplete={scrollToTop}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageWrapper><Index /></PageWrapper>} />
          <Route path="/blogs" element={<PageWrapper><Blog /></PageWrapper>} />
          <Route path="/blogs/:slug" element={<PageWrapper><BlogDetail /></PageWrapper>} />
          {/* Old /blog paths (Vercel 308s them too; this covers dev and in-app history) */}
          <Route path="/blog" element={<Navigate to="/blogs" replace />} />
          <Route path="/blog/:slug" element={<OldBlogRedirect />} />
          <Route path="/projects" element={<PageWrapper><ProjectsPage /></PageWrapper>} />
          <Route path="/experience" element={<PageWrapper><ExperiencePage /></PageWrapper>} />
          <Route path="/certifications" element={<PageWrapper><CertificationsPage /></PageWrapper>} />
          <Route path="/skills" element={<PageWrapper><SkillsPage /></PageWrapper>} />
          <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
          <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
        </Routes>
      </AnimatePresence>
    </div>
  );
};

const App = () => {
  React.useEffect(() => {
    const playClickSound = () => {
      try {
        const audio = new Audio(clickSound);
        audio.volume = 0.4;
        audio.play().catch(() => {});
      } catch (err) {
        // Audio might be blocked or not supported
      }
    };

    const handleGlobalClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('button, a, [role="button"]');
      if (target) {
        // Haptic feedback
        if ('vibrate' in navigator) {
          navigator.vibrate(5);
        }
        
        // Synthesized sound feedback
        playClickSound();
      }
    };

    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  return (
    <MotionConfig reducedMotion="user">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <CustomCursor />
        <SmoothScroll />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Header />
          <AnimatedRoutes />
          <Footer />
        </BrowserRouter>
        <Analytics />

      </TooltipProvider>
    </QueryClientProvider>
    </MotionConfig>
  );
};

export default App;
